<script setup lang="ts">
  // External library imports
  import type { PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { type GoogleCredentialResponse, LoginEvents, type LoginCallbackPayload, type ScreenChangeData } from '../../types'

  import { getConfig } from '../../../core-layer'

  // Type imports

  // Props definitions
  const props = defineProps({
    loginType: {
      type: String,
      required: true,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: ScreenChangeData): void
    (e: 'loginCallback', value: LoginCallbackPayload): void
    (e: 'updateLoader', value: boolean): void
  }>()

  // Composables
  const { t } = useI18n()

  // Refs and reactive variables
  const isLoading = ref(true)

  const GSI_MAX_BUTTON_WIDTH = 400

  const GSI_MIN_BUTTON_WIDTH = 240

  let gsiHostObserver: MutationObserver | undefined

  let gsiRelayoutTimer: ReturnType<typeof setTimeout> | undefined

  let lastGsiWidth = 0

  onUnmounted(() => {
    gsiHostObserver?.disconnect()
    if (gsiRelayoutTimer) clearTimeout(gsiRelayoutTimer)
  })

  // Lifecycle hooks
  onMounted(() => {
    // Wait for Google script to be available (loaded in nuxt.config.ts head)
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait time

    const checkGoogle = () => {
      attempts++

      if (window.google?.accounts) {
        initializeGoogleSSO()
      } else if (attempts < maxAttempts) {
        // Retry after a short delay
        setTimeout(checkGoogle, 100)
      } else {
        // Timeout - hide loading and show error
        isLoading.value = false
        emit('updateLoader', false)
        loadGoogleScriptManually()
      }
    }
    checkGoogle()
  })

  function gsiButtonWidth(host: HTMLElement): number {
    return Math.min(GSI_MAX_BUTTON_WIDTH, Math.max(GSI_MIN_BUTTON_WIDTH, Math.floor(host.getBoundingClientRect().width) || 320))
  }

  /**
   * Personalized "Continue as …" injects an empty sibling before the iframe.
   * Hide it so it cannot take layout space. Leave iframe margin alone — GSI
   * sets `margin: -2px -10px` so the painted button matches `width`.
   */
  function hideGsiSpacers(host: HTMLElement) {
    const wrapper = host.querySelector('.S9gUrf-YoZ4jf')
    if (!wrapper) return
    for (const child of Array.from(wrapper.children)) {
      if (child instanceof HTMLElement && child.tagName !== 'IFRAME') {
        child.style.setProperty('display', 'none', 'important')
      }
    }
  }

  function renderGsiButton(host: HTMLElement, origin: string) {
    const width = gsiButtonWidth(host)
    lastGsiWidth = width
    window.google?.accounts?.id?.renderButton(host, {
      theme: 'outline',
      logo_alignment: 'center',
      size: 'large',
      width,
      shape: 'rectangular',
      state_cookie_domain: origin,
      text: 'continue_with',
      type: 'standard',
    })
    hideGsiSpacers(host)
  }

  function watchGsiHost(host: HTMLElement) {
    gsiHostObserver?.disconnect()
    hideGsiSpacers(host)
    gsiHostObserver = new MutationObserver(() => hideGsiSpacers(host))
    gsiHostObserver.observe(host, { childList: true, subtree: true })
  }

  // Declare google global (Google Identity Services / GSI client)
  declare global {
    interface Window {
      google?: {
        accounts?: {
          id?: {
            initialize: (config: {
              client_id: string
              callback: (response: GoogleCredentialResponse) => void
              auto_select?: boolean
              use_fedcm_for_prompt?: boolean
              state_cookie_domain?: string
            }) => void
            renderButton: (element: HTMLElement, options: Record<string, string | number>) => void
          }
        }
      }
    }
  }

  // Fallback function to load Google script manually
  function loadGoogleScriptManually() {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      setTimeout(() => {
        if (window.google?.accounts) {
          initializeGoogleSSO()
        }
      }, 100)
    }
    script.onerror = () => {
      console.error('Failed to load Google script manually')
    }
    document.head.appendChild(script)
  }

  // User-defined functions
  /**
   * Usage:
   * After user is logged in using SSO, remove the referral code from local storage immediately
   * to avoid any future use of it just in case the login callback event is not emitted
   */
  function updateRefLocalStorage() {
    // window.localStorage.removeItem('refCode')
  }

  function initializeGoogleSSO() {
    emit('updateLoader', true)

    // Get the current origin for cookie domain
    const currentOrigin = window.location.origin

    try {
      window.google?.accounts?.id?.initialize({
        client_id: getConfig()?.KOLLAB_GOOGLE_CLIENT_ID || '',
        callback: handleCredentialResponse,
        auto_select: true, // Automatically prompt users who are previously signed in
        use_fedcm_for_prompt: true,
        state_cookie_domain: currentOrigin,
      })

      // Hide loading state FIRST so the buttonDiv becomes visible
      isLoading.value = false
      emit('updateLoader', false)

      // Wait for DOM to update before trying to render the button
      setTimeout(() => {
        const buttonDiv = document.getElementById('buttonDiv')

        if (buttonDiv) {
          renderGsiButton(buttonDiv, currentOrigin)
          watchGsiHost(buttonDiv)
          // Modal width can still be settling; one extra measure, not a ResizeObserver.
          gsiRelayoutTimer = setTimeout(() => {
            const host = document.getElementById('buttonDiv')
            if (!host) return
            if (Math.abs(gsiButtonWidth(host) - lastGsiWidth) < 8) return
            renderGsiButton(host, currentOrigin)
          }, 300)
        } else {
          console.error('Button div not found!')
        }
      }, 100) // Small delay to ensure DOM is updated
    } catch (error) {
      console.error('Error initializing Google SSO:', error)
      isLoading.value = false
      emit('updateLoader', false)
    }

    // window.google.accounts.id.prompt() // Trigger the Google One Tap directly
  }

  async function handleCredentialResponse(response: GoogleCredentialResponse) {
    if (!response.credential) return

    emit('updateLoader', true)
    try {
      const { data } = await useAuth().loginWithGoogle(response.credential)

      if (data?.token) {
        if (props.referrerUser?._id) {
          updateRefLocalStorage()
        }
        emit(LoginEvents.LOGIN_CALLBACK, {
          eventType: LoginEvents.LOGIN_SUCCESSFUL,
          ...data,
        })
      }
    } catch (error) {
      console.error('Error handling credential response:', error)
    } finally {
      emit('updateLoader', false)
    }
  }
</script>

<template>
  <div>
    <div class="flex w-full items-center justify-center">
      <div v-if="isLoading" id="google-sso-loading" class="google-button custom-google-button flex w-full items-center justify-center">
        <div class="h-4 w-4 animate-spin rounded-full border-2 border-default border-t-primary"></div>
        <span class="ml-2 text-sm text-600">{{ t('auth.loadingGoogleSignIn') }}</span>
      </div>
      <div v-else id="buttonDiv" class="google-button custom-google-button w-full"></div>
    </div>

    <CPDivider class="my-4">
      <CPText :id="createElementId('login-modal', 'with-email', 'divider')" class="text-secondary" size="xs" weight="medium">
        {{ loginType === 'SIGN_IN' ? t('auth.orLoginWithYourEmail') : t('auth.orSignUpWithYourEmail') }}
      </CPText>
    </CPDivider>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 12px 0;
  }

  .custom-google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 44px;
  }

  .google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .custom-google-button iframe {
    height: 44px !important;
  }

  .form-item {
    height: 62px !important;
  }

  #buttonDiv {
    width: 100%;
  }
</style>

<style>
  /*
   * Unscoped on purpose. Google Identity Services injects .S9gUrf-YoZ4jf into
   * #buttonDiv after mount. Scoped / :deep() cannot style those nodes.
   *
   * Do not set iframe { width: 100% }. That stretches the frame only; the inner
   * GSI button stays content-sized, so the mark and text look tiny in a wide box.
   * Pass the container pixel width into renderButton instead.
   *
   * Do not stretch .S9gUrf-YoZ4jf to 100%. Personalized "Continue as …" paints
   * an iframe at width+20 with margin: -2px -10px. A full-width wrapper then
   * left-aligns that iframe, so the button sits 10px left of the form fields.
   * Keep the wrapper shrink-wrapped and centered via #buttonDiv flex.
   *
   * Do not zero iframe margin. GSI's negative margin is how the painted button
   * matches the width we passed to renderButton.
   */
  .S9gUrf-YoZ4jf > div:empty,
  .S9gUrf-YoZ4jf > div:first-child:not(iframe) {
    display: none !important;
  }
</style>
